import React, { useEffect } from 'react'

import { useDashboardFeatureFlags } from '@components'
import { Dashboard, PageLayout } from '@components/common'
import { SearchBar } from '@components/common/SearchBar'
import { DashboardPermissionGuard } from '@components/utils/DashboardPermissions'
import { useWebviewEventsBus } from '@contexts/WebviewEventsBusContext'
import type { TableData } from '@fourthwall/components'
import {
  Banner,
  Button,
  ButtonGroup,
  HeadContent,
  Pagination,
  ToolbarButton,
  useMediaQueryContext,
  useModalContext,
} from '@fourthwall/components'
import { PlusIcon } from '@fourthwall/icons'

import { useCreateProduct } from '../Product/hooks/useCreateProduct'
import { useSearchQuery } from '../hooks/useSearchQuery'
import {
  AddSubscriptionModal,
  UpgradeSubscriptionBanner,
  useSubscription,
} from '../modules/Subscriptions'
import { useTopSubscriptionPlan } from '../modules/Subscriptions/hooks/useTopSubscriptionPlan'
import { ProductsEmptyState } from './components/ProductsEmptyState'
import { ProductsEmptyStateWithRecommendations } from './components/ProductsEmptyStateWithRecommendations'
import { useFilterProducts } from './hooks/useFilterProducts'
import { ProductsData } from './modules/ProductsData'
import { ProductsFilter } from './modules/ProductsFilter/ProductsFilter'
import * as S from './styled'
import type { Product } from './types'

export type ProductsViewBetaProps = {
  products: Product[]
  isLoading: boolean
  totalPages?: number | null
  totalProducts?: number | null
  currentPage: number
  onPageChange: (newPage: number) => void
  onProductPositionChange: (oldIndex: number, newIndex: number) => void
  onProductPositionSet: (rowData: TableData) => void
  onDuplicateOffer: (rowData: TableData) => void
  onArchiveOffer: (rowData: TableData) => void
  onUnarchiveOffer: (rowData: TableData) => void
  onArchiveManyOffers: (offerIds: string[], cb?: () => void) => void
  onToggleMultipleOfferStatus: (offerIds: string[], cb?: () => void) => void
}

export const ProductsView = ({
  products,
  isLoading,
  totalPages,
  totalProducts,
  currentPage,
  onPageChange,
  onProductPositionChange,
  onProductPositionSet,
  onDuplicateOffer,
  onArchiveOffer,
  onUnarchiveOffer,
  onArchiveManyOffers,
  onToggleMultipleOfferStatus,
}: ProductsViewBetaProps) => {
  const media = useMediaQueryContext()
  const { open } = useModalContext()
  const { searchQuery, searchValue, resetSearchValue, setSearchValue, searchOptions } =
    useSearchQuery()
  const { hasFilterParams } = useFilterProducts()
  const { openCreateProductModal } = useCreateProduct()
  const { canUpgradeSubscription, offers, subscriptionData } = useSubscription()
  const areSearchOrFiltersParamsApplied = searchQuery || hasFilterParams
  const { getTopSubscriptionPlanLimitLabel } = useTopSubscriptionPlan()
  const offersLimitLabel = getTopSubscriptionPlanLimitLabel('offers')
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const webviewEventBus = useWebviewEventsBus()

  const handleUpgradeSubscription = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
    open(AddSubscriptionModal, {
      title: `Upgrade your plan to offer more than ${subscriptionData?.offers.limit} products`,
      source: 'product_list',
      onSuccess,
    })
  }

  const handleCreateProductClick = () => {
    openCreateProductModal()
  }

  useEffect(() => {
    const removeEventListener = webviewEventBus.addEventListener('openCreateProductModal', () => {
      handleCreateProductClick()
    })

    return removeEventListener
  }, [])

  const renderSubscriptionBanner = () => {
    if (!offers.hasLimit || !subscriptionData || !dashboardFeatureFlags.subscriptions.enabled)
      return null

    // Usage < Limit - Threshold
    if (subscriptionData.offers.usage < subscriptionData.offers.limit - offers.threshold) {
      return null
    }

    // Limit - Threshold < Usage < Limit
    if (
      subscriptionData.offers.usage >= subscriptionData.offers.limit - offers.threshold &&
      offers.canCreate
    ) {
      if (!canUpgradeSubscription) {
        return (
          <Dashboard.Section.Item>
            <Banner appearance="pro">
              You've created {subscriptionData.offers.usage} out of {subscriptionData.offers.limit}{' '}
              products allowed on your current plan. To stay within the limit, consider archiving
              any unused products or{' '}
              <a href="mailto:support@fourthwall.com">contact our support team</a>.
            </Banner>
          </Dashboard.Section.Item>
        )
      }

      return (
        <Dashboard.Section.Item>
          <UpgradeSubscriptionBanner source="product_list" onClick={handleUpgradeSubscription}>
            You’ve created {subscriptionData.offers.usage} out of your{' '}
            {subscriptionData.offers.limit}-product limit. Upgrade to{' '}
            <strong>Fourthwall Pro</strong> to offer {offersLimitLabel} products.
          </UpgradeSubscriptionBanner>
        </Dashboard.Section.Item>
      )
    }

    // Usage >= Limit
    if (!offers.canCreate) {
      if (!canUpgradeSubscription) {
        return (
          <Dashboard.Section.Item>
            <Banner appearance="pro">
              You've created {subscriptionData.offers.usage} out of {subscriptionData.offers.limit}{' '}
              products allowed on your current plan. To add new products, please archive some
              existing ones or <a href="mailto:support@fourthwall.com">contact our support team</a>.
            </Banner>
          </Dashboard.Section.Item>
        )
      }

      return (
        <Dashboard.Section.Item>
          <UpgradeSubscriptionBanner source="product_list" onClick={handleUpgradeSubscription}>
            You’ve hit your {subscriptionData?.offers.limit}-product limit. Upgrade to{' '}
            <strong>Fourthwall Pro</strong> to offer {offersLimitLabel} products.
          </UpgradeSubscriptionBanner>
        </Dashboard.Section.Item>
      )
    }

    return null
  }

  const renderSearchBar = () => (
    <SearchBar
      name="search"
      placeholder="Search products"
      value={searchValue}
      maxLength={searchOptions.maxLength}
      onValueChange={(value: string) => setSearchValue(value)}
      onActionIconClick={resetSearchValue}
    />
  )

  const getToolbarContent = () => {
    if (!media.tablet) {
      return (
        <ButtonGroup space="medium">
          <DashboardPermissionGuard permission="products.create">
            <ToolbarButton
              label="Create new product"
              icon={PlusIcon}
              onClick={handleCreateProductClick}
            />
          </DashboardPermissionGuard>
          <ProductsFilter />
        </ButtonGroup>
      )
    }

    return (
      <ButtonGroup space="medium">
        <ProductsFilter />
        <S.SearchBarWrapper>{renderSearchBar()}</S.SearchBarWrapper>
        <DashboardPermissionGuard permission="products.create">
          <Button
            label="Create new product"
            appearance="primary"
            size="small"
            onClick={handleCreateProductClick}
          />
        </DashboardPermissionGuard>
      </ButtonGroup>
    )
  }

  const renderEmptyState = () => {
    if (areSearchOrFiltersParamsApplied) {
      return <ProductsEmptyState />
    }

    return <ProductsEmptyStateWithRecommendations />
  }

  return (
    <PageLayout size="large">
      <HeadContent title="Products" />
      <Dashboard.Section
        name={`Products${totalProducts ? ` (${totalProducts})` : ''}`}
        toolbarContent={getToolbarContent()}
        bottomContent={!media.tablet && renderSearchBar()}
      >
        {renderSubscriptionBanner()}

        {products.length > 0 || isLoading ? (
          <>
            <Dashboard.Section.Item>
              <ProductsData
                products={products}
                currentPage={currentPage}
                isLoading={isLoading}
                onArchiveSelectedOffersClick={onArchiveManyOffers}
                onDuplicateOffer={onDuplicateOffer}
                onArchiveOffer={onArchiveOffer}
                onUnarchiveOffer={onUnarchiveOffer}
                onProductPositionSet={onProductPositionSet}
                onProductPositionChange={onProductPositionChange}
                onToggleMultipleOfferStatus={onToggleMultipleOfferStatus}
              />
            </Dashboard.Section.Item>
            {!!totalPages && !!products.length && (
              <Dashboard.Section.Item>
                <Pagination
                  pageCount={totalPages}
                  currentPage={currentPage}
                  onChange={onPageChange}
                />
              </Dashboard.Section.Item>
            )}
          </>
        ) : (
          <Dashboard.Section.Item>{renderEmptyState()}</Dashboard.Section.Item>
        )}
      </Dashboard.Section>
    </PageLayout>
  )
}
